import { useMemo } from "react";

/**
 * Render trusted (admin-authored) SVG markup inline without recreating it on
 * every render.
 *
 * `dangerouslySetInnerHTML` takes an object, and React 19 re-assigns
 * `innerHTML` whenever that object's identity changes — an inline
 * `{ __html: svg }` literal is new on each render, so the SVG was torn down and
 * rebuilt every time the parent re-rendered (a visible blink on every keystroke
 * of an autosaving form). React 18 compared the strings and left the DOM alone.
 * Memoising the object on the string restores that behaviour.
 */
export function useTrustedHtml(html: string | null | undefined): { __html: string } {
  return useMemo(() => ({ __html: html ?? "" }), [html]);
}

/** Alias of `useTrustedHtml` for the SVG icon sites. */
export const useInlineSvgHtml = useTrustedHtml;

type InlineSvgProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  "children" | "dangerouslySetInnerHTML"
> & {
  /** Raw SVG markup from a trusted source (a tenant admin's icon upload). */
  svg: string | null | undefined;
  /** Host element. Icons are `span`s; the icon field's swatch and picker cells need a `div` / `button`. */
  as?: "span" | "div" | "button";
  type?: "button" | "submit";
};

/** A decorative-by-default inline SVG host; pass `aria-hidden={false}` (and a label) when it is not. */
export function InlineSvg({ svg, as = "span", ...props }: InlineSvgProps) {
  const html = useTrustedHtml(svg);
  const Tag = as;
  return (
    <Tag
      aria-hidden="true"
      {...props}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted admin-authored SVG markup (see the doc comment)
      dangerouslySetInnerHTML={html}
    />
  );
}
